import { createFileRoute } from '@tanstack/react-router'
import { RouteErrorFallback } from '@/components/ErrorBoundary'
import { useState, useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Switch } from '@/components/ui/switch'
import { Checkbox } from '@/components/ui/checkbox'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from '@/components/ui/dialog'
import { Tabs, TabsList, TabsTrigger, TabsContent } from '@/components/ui/tabs'
import { RadioGroup, RadioGroupItem } from '@/components/ui/radio-group'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { TablePagination } from '@/components/ui/table-pagination'
import { Badge } from '@/components/ui/badge'
import { Alert, AlertDescription } from '@/components/ui/alert'
import { DeleteButton, EditButton } from '@/components/ui/action-buttons'
import {
  PageContainer,
  PageCard,
  PageHeader,
  PageHeaderContent,
  PageHeaderTitle,
  PageHeaderHeading,
  PageHeaderDescription,
  PageHeaderActions,
  PageContent
} from '@/components/ui/page'
import { Loader2, Plus, Lock, AlertTriangle } from 'lucide-react'
import { CopyButton } from '@/components/widget-keys/CopyButton'
import { ColorPickerField } from '@/components/widget-keys/ColorPickerField'
import { LogoUpload } from '@/components/widget-keys/LogoUpload'
import {
  WidgetPreviewMock,
  WIDGET_DEFAULT_BUBBLE_COLOR,
  WIDGET_DEFAULT_AGENT_MESSAGE_COLOR,
  WIDGET_DEFAULT_USER_MESSAGE_COLOR,
  WIDGET_DEFAULT_BACKGROUND_COLOR,
  WIDGET_DEFAULT_POSITION,
} from '@/components/widget-keys/WidgetPreviewMock'
import { contrastText } from '@/widget/branding'

// Hooks
import { useWidgetKeys, useWidgetKey, type WidgetKey } from '@/hooks/useWidgetKeys'
import { env } from '@/lib/env'
import { useWidgetKeyMutations } from '@/hooks/useWidgetKeyMutations'
import { useAgents } from '@/hooks/useAgents'
import { useAPIClient } from '@/hooks/useApi'

// Components
import { AllowedOriginsInput } from '@/components/widget-keys/AllowedOriginsInput'

// Types
import { ChannelType } from '@/api/generated/models'
import type { WidgetKeyCreateDTO, WidgetKeyUpdateDTO, WidgetKeyResponseDTO } from '@/api/generated/models'

export const Route = createFileRoute('/chat-widgets')({
  component: ChatWidgets,
  errorComponent: RouteErrorFallback,
})

const embedSnippet = (key: string) =>
  `<script src="${env.APP_ORIGIN}/widget.js" data-widget-key="${key}" async></script>`


function ChatWidgets() {
  // State
  const [currentPage, setCurrentPage] = useState(1)
  const [pageSize, setPageSize] = useState(25)
  const [createDialogOpen, setCreateDialogOpen] = useState(false)
  const [detailDialogOpen, setDetailDialogOpen] = useState(false)
  const [dialogMode, setDialogMode] = useState<'view' | 'edit'>('view')
  const [detailTab, setDetailTab] = useState<'settings' | 'design'>('settings')
  const [selectedId, setSelectedId] = useState<string | null>(null)

  // Create dialog state
  const [createStep, setCreateStep] = useState<'form' | 'success'>('form')
  const [createLabel, setCreateLabel] = useState('')
  const [createAgentId, setCreateAgentId] = useState('')
  const [createOrigins, setCreateOrigins] = useState<string[]>([])
  const [activateChat, setActivateChat] = useState(true)
  const [createdKey, setCreatedKey] = useState<WidgetKeyResponseDTO | null>(null)

  // Edit dialog state
  const [editLabel, setEditLabel] = useState('')
  const [editOrigins, setEditOrigins] = useState<string[]>([])
  const [editActive, setEditActive] = useState(true)

  // Design tab state
  const [bubbleColor, setBubbleColor] = useState<string | null>(null)
  const [position, setPosition] = useState<string>(WIDGET_DEFAULT_POSITION)
  const [agentMsgColor, setAgentMsgColor] = useState<string | null>(null)
  const [userMsgColor, setUserMsgColor] = useState<string | null>(null)
  const [agentMsgTextColor, setAgentMsgTextColor] = useState<string | null>(null)
  const [userMsgTextColor, setUserMsgTextColor] = useState<string | null>(null)
  const [panelBgColor, setPanelBgColor] = useState<string | null>(null)
  const [panelTextColor, setPanelTextColor] = useState<string | null>(null)
  const [logoFile, setLogoFile] = useState<File | null>(null)
  const [logoRemoveRequested, setLogoRemoveRequested] = useState(false)
  const [logoPreviewUrl, setLogoPreviewUrl] = useState<string | null>(null)

  const offset = (currentPage - 1) * pageSize

  // Queries
  const { data, isLoading, error } = useWidgetKeys(pageSize, offset)
  const widgetKeys = data?.items ?? []
  const { data: agents = [] } = useAgents()
  const { data: selectedKey } = useWidgetKey(selectedId)

  // Mutations
  const { createWidgetKey, updateWidgetKey, deleteWidgetKey, uploadWidgetKeyLogo, deleteWidgetKeyLogo } = useWidgetKeyMutations()
  const { agentsApi } = useAPIClient()
  const queryClient = useQueryClient()

  const agentName = (agentId: string) => agents.find((a) => a.id === agentId)?.name ?? agentId
  const agentById = (agentId: string) => agents.find((a) => a.id === agentId)

  // logo_url is backend-relative; resolve against the API host, not the dashboard origin
  const selectedLogoUrl = selectedKey?.logo_url
    ? new URL(selectedKey.logo_url, env.API_BASE_URL).toString()
    : null

  const designSavingRef = useRef(false)

  // Populate edit form when full key is loaded
  useEffect(() => {
    if (selectedKey && !designSavingRef.current) {
      setEditLabel(selectedKey.label)
      setEditOrigins(selectedKey.allowed_origins)
      setEditActive(selectedKey.is_active)
      setBubbleColor(selectedKey.bubble_color ?? null)
      setPosition(selectedKey.position ?? WIDGET_DEFAULT_POSITION)
      setAgentMsgColor(selectedKey.agent_message_color ?? null)
      setUserMsgColor(selectedKey.user_message_color ?? null)
      setAgentMsgTextColor(selectedKey.agent_message_text_color ?? null)
      setUserMsgTextColor(selectedKey.user_message_text_color ?? null)
      setPanelBgColor(selectedKey.background_color ?? null)
      setPanelTextColor(selectedKey.text_color ?? null)
      setLogoFile(null)
      setLogoRemoveRequested(false)
    }
  }, [selectedKey])

  useEffect(() => {
    if (!logoFile) {
      setLogoPreviewUrl(null)
      return
    }
    const url = URL.createObjectURL(logoFile)
    setLogoPreviewUrl(url)
    return () => URL.revokeObjectURL(url)
  }, [logoFile])

  const totalItems = data?.total_count ?? 0
  const totalPages = Math.max(1, Math.ceil(totalItems / pageSize))

  const handlePageSizeChange = (newPageSize: number) => {
    setPageSize(newPageSize)
    setCurrentPage(1)
  }

  const activeAgents = agents.filter((a) => a.is_active)

  const enableChatChannel = async (agentId: string) => {
    const agent = agentById(agentId)
    if (!agent) return
    if (agent.enabled_channels.includes(ChannelType.Chat)) return
    await agentsApi.updateAgentApiV1AgentsAgentIdPatch(agentId, {
      enabled_channels: [...agent.enabled_channels, ChannelType.Chat]
    })
    await queryClient.invalidateQueries({ queryKey: ['agents'] })
  }

  const resetCreate = () => {
    setCreateStep('form')
    setCreateLabel('')
    setCreateAgentId('')
    setCreateOrigins([])
    setActivateChat(true)
    setCreatedKey(null)
  }

  const openCreate = () => {
    resetCreate()
    setCreateDialogOpen(true)
  }

  const handleCreateSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    const createData: WidgetKeyCreateDTO = {
      agent_id: createAgentId,
      label: createLabel,
      allowed_origins: createOrigins
    }

    try {
      const created = await createWidgetKey.mutateAsync(createData)
      const agent = agentById(createAgentId)
      if (activateChat && agent && !agent.enabled_channels.includes(ChannelType.Chat)) {
        await enableChatChannel(createAgentId)
      }
      setCreatedKey(created)
      setCreateStep('success')
    } catch (err) {
      console.error('Failed to create widget key:', err)
    }
  }

  const handleRowClick = (widgetKey: WidgetKey) => {
    setSelectedId(widgetKey.id)
    setEditLabel(widgetKey.label)
    setEditOrigins(widgetKey.allowed_origins)
    setEditActive(widgetKey.is_active)
    setDialogMode('view')
    setDetailTab('settings')
    setDetailDialogOpen(true)
  }

  const handleEditSubmit = async (e: React.FormEvent) => {
    e.preventDefault()
    if (!selectedId) return

    const updateData: WidgetKeyUpdateDTO = {
      label: editLabel,
      allowed_origins: editOrigins,
      is_active: editActive
    }

    try {
      await updateWidgetKey.mutateAsync({ id: selectedId, data: updateData })
      setDialogMode('view')
    } catch (err) {
      console.error('Failed to update widget key:', err)
    }
  }

  const handleReactivate = async () => {
    if (!selectedId) return
    try {
      await updateWidgetKey.mutateAsync({ id: selectedId, data: { is_active: true } })
      toast.success('Widget reaktiviert')
    } catch (err) {
      console.error('Failed to reactivate widget key:', err)
    }
  }

  const handleDelete = async () => {
    if (!selectedId) return
    try {
      await deleteWidgetKey.mutateAsync(selectedId)
      setDetailDialogOpen(false)
      setSelectedId(null)
    } catch (err) {
      console.error('Failed to revoke widget key:', err)
    }
  }

  const designDirty =
    !!selectedKey &&
    (bubbleColor !== (selectedKey.bubble_color ?? null) ||
      position !== (selectedKey.position ?? WIDGET_DEFAULT_POSITION) ||
      agentMsgColor !== (selectedKey.agent_message_color ?? null) ||
      userMsgColor !== (selectedKey.user_message_color ?? null) ||
      agentMsgTextColor !== (selectedKey.agent_message_text_color ?? null) ||
      userMsgTextColor !== (selectedKey.user_message_text_color ?? null) ||
      panelBgColor !== (selectedKey.background_color ?? null) ||
      panelTextColor !== (selectedKey.text_color ?? null) ||
      logoFile !== null ||
      logoRemoveRequested)

  const resetDesignState = () => {
    if (!selectedKey) return
    setBubbleColor(selectedKey.bubble_color ?? null)
    setPosition(selectedKey.position ?? WIDGET_DEFAULT_POSITION)
    setAgentMsgColor(selectedKey.agent_message_color ?? null)
    setUserMsgColor(selectedKey.user_message_color ?? null)
    setAgentMsgTextColor(selectedKey.agent_message_text_color ?? null)
    setUserMsgTextColor(selectedKey.user_message_text_color ?? null)
    setPanelBgColor(selectedKey.background_color ?? null)
    setPanelTextColor(selectedKey.text_color ?? null)
    setLogoFile(null)
    setLogoRemoveRequested(false)
  }

  const handleResetDesignToDefault = () => {
    setBubbleColor(null)
    setPosition(WIDGET_DEFAULT_POSITION)
    setAgentMsgColor(null)
    setUserMsgColor(null)
    setAgentMsgTextColor(null)
    setUserMsgTextColor(null)
    setPanelBgColor(null)
    setPanelTextColor(null)
    setLogoFile(null)
    if (selectedKey?.logo_url) {
      setLogoRemoveRequested(true)
    }
  }

  const isDesignSaving = uploadWidgetKeyLogo.isPending || deleteWidgetKeyLogo.isPending || updateWidgetKey.isPending

  const handleDesignSave = async () => {
    if (!selectedId) return
    designSavingRef.current = true
    try {
      if (logoFile) {
        await uploadWidgetKeyLogo.mutateAsync({ id: selectedId, file: logoFile })
      } else if (logoRemoveRequested) {
        await deleteWidgetKeyLogo.mutateAsync(selectedId)
      }
    } catch (err: any) {
      designSavingRef.current = false
      console.error('Failed to update widget logo:', err)
      const detail = err?.response?.data?.detail
      toast.error(typeof detail === 'string' ? detail : 'Logo konnte nicht gespeichert werden.')
      return
    }

    try {
      await updateWidgetKey.mutateAsync({
        id: selectedId,
        data: {
          bubble_color: bubbleColor,
          position,
          agent_message_color: agentMsgColor,
          user_message_color: userMsgColor,
          agent_message_text_color: agentMsgTextColor,
          user_message_text_color: userMsgTextColor,
          background_color: panelBgColor,
          text_color: panelTextColor
        }
      })
      setLogoFile(null)
      setLogoRemoveRequested(false)
      toast.success('Design gespeichert')
    } catch (err) {
      console.error('Failed to update widget design:', err)
      toast.error('Design konnte nicht gespeichert werden.')
    } finally {
      designSavingRef.current = false
    }
  }

  const handleActivateChatFromDetail = async () => {
    if (!selectedKey) return
    try {
      await enableChatChannel(selectedKey.agent_id)
      toast.success('Chat-Kanal aktiviert')
    } catch (err) {
      console.error('Failed to activate chat channel:', err)
    }
  }

  const statusBadge = (widgetKey: Pick<WidgetKey, 'is_active' | 'allowed_origins'>) => {
    if (!widgetKey.is_active) {
      return <Badge variant="secondary">Widerrufen</Badge>
    }
    if (widgetKey.allowed_origins.length === 0) {
      return (
        <Badge variant="outline" className="bg-amber-100 text-amber-800 hover:bg-amber-100">
          Inert
        </Badge>
      )
    }
    return <Badge variant="default">Aktiv</Badge>
  }

  const createAgent = agentById(createAgentId)
  const createAgentHasChat = createAgent?.enabled_channels.includes(ChannelType.Chat) ?? false

  const detailAgent = selectedKey ? agentById(selectedKey.agent_id) : undefined
  const detailAgentHasChat = detailAgent?.enabled_channels.includes(ChannelType.Chat) ?? true

  return (
    <PageContainer>
      <PageCard>
        <PageHeader>
          <PageHeaderContent>
            <PageHeaderTitle>
              <PageHeaderHeading>Chat-Widgets</PageHeaderHeading>
              <PageHeaderDescription>
                Betten Sie Ihre Chat-Agenten als Widget auf Websites ein.
              </PageHeaderDescription>
            </PageHeaderTitle>
            <PageHeaderActions>
              <Button onClick={openCreate}>
                <Plus className="mr-2 h-4 w-4" />
                Neues Widget
              </Button>
            </PageHeaderActions>
          </PageHeaderContent>
        </PageHeader>
        <PageContent className="p-6 overflow-auto">
          {isLoading && (
            <div className="flex items-center justify-center h-64">
              <Loader2 className="h-8 w-8 animate-spin text-muted-foreground" />
            </div>
          )}

          {error && (
            <div className="flex items-center justify-center h-32 text-red-600">
              <span>Fehler beim Laden der Chat-Widgets.</span>
            </div>
          )}

          {!isLoading && !error && widgetKeys.length === 0 && (
            <div className="flex items-center justify-center h-32 text-gray-500">
              <span>Noch keine Chat-Widgets vorhanden.</span>
            </div>
          )}

          {!isLoading && !error && widgetKeys.length > 0 && (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Name</TableHead>
                  <TableHead>Agent</TableHead>
                  <TableHead>Widget Key</TableHead>
                  <TableHead>Domains</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {widgetKeys.map((widgetKey) => (
                  <TableRow
                    key={widgetKey.id}
                    className="cursor-pointer hover:bg-muted/50"
                    onClick={() => handleRowClick(widgetKey)}
                  >
                    <TableCell className="font-medium">{widgetKey.label}</TableCell>
                    <TableCell>{agentName(widgetKey.agent_id)}</TableCell>
                    <TableCell>
                      <div className="flex items-center gap-1.5">
                        <span className="font-mono text-sm">{widgetKey.key.slice(0, 10)}…</span>
                        <CopyButton
                          value={widgetKey.key}
                          label="Widget Key kopieren"
                          variant="ghost"
                          className="h-6 w-6"
                          iconClassName="h-3.5 w-3.5"
                        />
                      </div>
                    </TableCell>
                    <TableCell>
                      {widgetKey.allowed_origins.length === 0 ? (
                        <span className="text-amber-600">⚠ 0</span>
                      ) : (
                        <span>{widgetKey.allowed_origins.length}</span>
                      )}
                    </TableCell>
                    <TableCell>{statusBadge(widgetKey)}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          )}
        </PageContent>

        {!isLoading && !error && widgetKeys.length > 0 && (
          <TablePagination
            currentPage={currentPage}
            totalPages={totalPages}
            totalItems={totalItems}
            pageSize={pageSize}
            onPageChange={setCurrentPage}
            onPageSizeChange={handlePageSizeChange}
          />
        )}
      </PageCard>

      {/* Create Dialog */}
      <Dialog open={createDialogOpen} onOpenChange={setCreateDialogOpen}>
        <DialogContent className="max-w-[calc(100vw-2rem)] sm:max-w-2xl max-h-[90dvh] overflow-y-auto">
          {createStep === 'form' ? (
            <>
              <DialogHeader>
                <DialogTitle>Neues Widget</DialogTitle>
                <DialogDescription>
                  Erstellen Sie einen Widget-Key, um einen Chat-Agenten auf einer Website einzubetten.
                </DialogDescription>
              </DialogHeader>

              <form onSubmit={handleCreateSubmit}>
                <div className="grid gap-4 mt-2">
                  <div className="grid gap-2">
                    <Label htmlFor="create-label">Name</Label>
                    <Input
                      id="create-label"
                      value={createLabel}
                      onChange={(e) => setCreateLabel(e.target.value)}
                      placeholder="z.B. Website Kundensupport"
                      required
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="create-agent">Agent</Label>
                    <Select value={createAgentId} onValueChange={setCreateAgentId}>
                      <SelectTrigger id="create-agent">
                        <SelectValue placeholder="Agent auswählen..." />
                      </SelectTrigger>
                      <SelectContent>
                        {activeAgents.map((agent) => (
                          <SelectItem key={agent.id} value={agent.id}>
                            {agent.name}
                          </SelectItem>
                        ))}
                      </SelectContent>
                    </Select>
                  </div>

                  <div className="grid gap-2">
                    <Label htmlFor="create-origins">Erlaubte Domains *</Label>
                    <AllowedOriginsInput
                      id="create-origins"
                      value={createOrigins}
                      onChange={setCreateOrigins}
                    />
                    <p className="text-sm text-muted-foreground">
                      Das Widget wird nur auf den angegebenen Domains geladen. Weitere können Sie später ergänzen.
                    </p>
                  </div>

                  {createAgentId && !createAgentHasChat && (
                    <div className="flex items-start space-x-2">
                      <Checkbox
                        id="create-activate-chat"
                        checked={activateChat}
                        onCheckedChange={(checked) => setActivateChat(checked === true)}
                        className="mt-0.5"
                      />
                      <div className="grid gap-1 leading-none">
                        <Label htmlFor="create-activate-chat">
                          Chat-Kanal für diesen Agenten aktivieren
                        </Label>
                        <p className="text-sm text-muted-foreground">
                          Ohne aktiven Chat-Kanal lädt das Widget nicht.
                        </p>
                      </div>
                    </div>
                  )}

                  {createAgentId && createAgentHasChat && (
                    <p className="text-sm text-muted-foreground">✓ Chat-Kanal aktiv</p>
                  )}
                </div>

                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={() => setCreateDialogOpen(false)}>
                    Abbrechen
                  </Button>
                  <Button type="submit" disabled={createWidgetKey.isPending || !createAgentId || createOrigins.length === 0}>
                    {createWidgetKey.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                    Erstellen
                  </Button>
                </DialogFooter>
              </form>
            </>
          ) : (
            <>
              <DialogHeader>
                <DialogTitle>✓ „{createdKey?.label}" wurde erstellt.</DialogTitle>
                <DialogDescription>
                  Key & Code sind jederzeit in den Details abrufbar.
                </DialogDescription>
              </DialogHeader>

              <div className="grid gap-4 mt-2">
                <div className="grid gap-2">
                  <Label>Widget Key</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-sm break-all">
                      {createdKey?.key}
                    </code>
                    <CopyButton value={createdKey?.key ?? ''} label="Widget Key kopieren" />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label>Embed-Code</Label>
                  <div className="flex items-start gap-2">
                    <code className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-sm break-all">
                      {createdKey && embedSnippet(createdKey.key)}
                    </code>
                    <CopyButton value={createdKey ? embedSnippet(createdKey.key) : ''} label="Embed-Code kopieren" />
                  </div>
                </div>
              </div>

              <DialogFooter className="mt-4">
                <Button
                  type="button"
                  onClick={() => {
                    setCreateDialogOpen(false)
                    resetCreate()
                  }}
                >
                  Fertig
                </Button>
              </DialogFooter>
            </>
          )}
        </DialogContent>
      </Dialog>

      {/* Detail/Edit Dialog */}
      <Dialog
        open={detailDialogOpen}
        onOpenChange={(open) => {
          setDetailDialogOpen(open)
          if (!open) resetDesignState()
        }}
      >
        <DialogContent className={`max-w-[calc(100vw-2rem)] ${detailTab === 'design' ? 'sm:max-w-4xl' : 'sm:max-w-2xl'} max-h-[90dvh] overflow-y-auto`}>
          <DialogHeader>
            <DialogTitle>
              {dialogMode === 'view' ? 'Widget-Details' : 'Widget bearbeiten'}
            </DialogTitle>
            <DialogDescription>
              {dialogMode === 'view' ? 'Details des Chat-Widgets anzeigen' : 'Widget-Einstellungen bearbeiten'}
            </DialogDescription>
          </DialogHeader>

          <Tabs value={detailTab} onValueChange={(v) => setDetailTab(v as 'settings' | 'design')}>
            <TabsList>
              <TabsTrigger value="settings">Einstellungen</TabsTrigger>
              <TabsTrigger value="design">Design</TabsTrigger>
            </TabsList>

            <TabsContent value="settings">
          {dialogMode === 'view' ? (
            <>
              <div className="grid gap-4 mt-2">
                <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
                  <div>
                    <Label className="text-muted-foreground">Name</Label>
                    <p className="font-medium">{selectedKey?.label}</p>
                  </div>
                  <div>
                    <Label className="text-muted-foreground">Status</Label>
                    <div className="mt-1">
                      {selectedKey && statusBadge(selectedKey)}
                    </div>
                  </div>
                </div>

                <div>
                  <Label className="text-muted-foreground">Agent</Label>
                  <div className="flex items-center gap-2 mt-1">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">
                      {selectedKey ? agentName(selectedKey.agent_id) : ''}
                    </span>
                  </div>
                </div>

                {selectedKey && selectedKey.allowed_origins.length > 0 && (
                  <div>
                    <Label className="text-muted-foreground">Erlaubte Domains</Label>
                    <div className="flex flex-wrap gap-2 mt-2">
                      {selectedKey.allowed_origins.map((origin) => (
                        <Badge key={origin} variant="outline">{origin}</Badge>
                      ))}
                    </div>
                  </div>
                )}

                <div className="grid gap-2">
                  <Label className="text-muted-foreground">Widget Key</Label>
                  <div className="flex items-center gap-2">
                    <code className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-sm break-all">
                      {selectedKey?.key}
                    </code>
                    <CopyButton value={selectedKey?.key ?? ''} label="Widget Key kopieren" />
                  </div>
                </div>

                <div className="grid gap-2">
                  <Label className="text-muted-foreground">Embed-Code</Label>
                  <div className="flex items-start gap-2">
                    <code className="flex-1 rounded-md bg-muted px-3 py-2 font-mono text-sm break-all">
                      {selectedKey && embedSnippet(selectedKey.key)}
                    </code>
                    <CopyButton value={selectedKey ? embedSnippet(selectedKey.key) : ''} label="Embed-Code kopieren" />
                  </div>
                </div>

                {selectedKey && selectedKey.allowed_origins.length === 0 && (
                  <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-amber-800">
                      Für diesen Widget-Key sind keine Domains hinterlegt — er lädt derzeit auf keiner Website. Fügen Sie mindestens eine Domain hinzu.
                    </AlertDescription>
                  </Alert>
                )}

                {selectedKey && !detailAgentHasChat && (
                  <Alert className="bg-amber-50 border-amber-200 text-amber-800">
                    <AlertTriangle className="h-4 w-4" />
                    <AlertDescription className="text-amber-800">
                      <span>
                        Der Chat-Kanal ist für diesen Agenten nicht aktiviert — das Widget lädt nicht.
                      </span>
                      <Button
                        type="button"
                        variant="outline"
                        size="sm"
                        className="mt-2"
                        onClick={handleActivateChatFromDetail}
                      >
                        Chat-Kanal aktivieren
                      </Button>
                    </AlertDescription>
                  </Alert>
                )}
              </div>

              <DialogFooter className="mt-4">
                {selectedKey?.is_active ? (
                  <>
                    <EditButton onClick={() => setDialogMode('edit')} />
                    <DeleteButton
                      onDelete={handleDelete}
                      itemName={selectedKey?.label || ''}
                      confirmationTitle="Widget widerrufen?"
                      confirmationDescription="Das Widget lädt danach auf keiner Website mehr. Sie können es später reaktivieren."
                    />
                  </>
                ) : (
                  <div className="flex items-center justify-between w-full gap-4">
                    <span className="text-sm text-muted-foreground">Dieses Widget ist widerrufen.</span>
                    <div className="flex gap-2">
                      <EditButton onClick={() => setDialogMode('edit')} />
                      <Button type="button" onClick={handleReactivate} disabled={updateWidgetKey.isPending}>
                        {updateWidgetKey.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                        Reaktivieren
                      </Button>
                    </div>
                  </div>
                )}
              </DialogFooter>
            </>
          ) : (
            <form onSubmit={handleEditSubmit}>
              <div className="grid gap-4 mt-2">
                <div className="grid gap-2">
                  <Label htmlFor="edit-label">Name</Label>
                  <Input
                    id="edit-label"
                    value={editLabel}
                    onChange={(e) => setEditLabel(e.target.value)}
                    required
                  />
                </div>

                <div className="grid gap-2">
                  <Label htmlFor="edit-origins">Erlaubte Domains *</Label>
                  <AllowedOriginsInput
                    id="edit-origins"
                    value={editOrigins}
                    onChange={setEditOrigins}
                  />
                </div>

                <div className="grid gap-2">
                  <Label className="text-muted-foreground">Agent</Label>
                  <div className="flex items-center gap-2">
                    <Lock className="h-4 w-4 text-muted-foreground" />
                    <span className="font-medium">
                      {selectedKey ? agentName(selectedKey.agent_id) : ''}
                    </span>
                  </div>
                  <p className="text-sm text-muted-foreground">
                    Zum Wechsel des Agenten legen Sie ein neues Widget an und widerrufen dieses.
                  </p>
                </div>

                <div className="flex items-center space-x-2">
                  <Switch
                    id="edit-active"
                    checked={editActive}
                    onCheckedChange={setEditActive}
                  />
                  <Label htmlFor="edit-active">Aktiv</Label>
                </div>
              </div>

              <DialogFooter className="mt-4">
                <Button type="button" variant="outline" onClick={() => setDialogMode('view')}>
                  Abbrechen
                </Button>
                <Button type="submit" disabled={updateWidgetKey.isPending || editOrigins.length === 0}>
                  {updateWidgetKey.isPending && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
              </DialogFooter>
            </form>
          )}
            </TabsContent>

            <TabsContent value="design">
              <div className="grid gap-6 mt-2 md:grid-cols-2">
                <div className="order-2 md:order-1 grid gap-4 content-start">
                  <ColorPickerField
                    label="Launcher-Button"
                    value={bubbleColor}
                    onChange={setBubbleColor}
                    defaultColor={WIDGET_DEFAULT_BUBBLE_COLOR}
                  />
                  <div className="grid gap-3 rounded-md border p-3">
                    <Label className="text-muted-foreground">Chat-Fenster</Label>
                    <ColorPickerField
                      label="Hintergrund"
                      value={panelBgColor}
                      onChange={setPanelBgColor}
                      defaultColor={WIDGET_DEFAULT_BACKGROUND_COLOR}
                    />
                    <ColorPickerField
                      label="Schrift"
                      value={panelTextColor}
                      onChange={setPanelTextColor}
                      defaultColor={contrastText(panelBgColor ?? WIDGET_DEFAULT_BACKGROUND_COLOR)}
                      placeholder="Auto"
                    />
                  </div>
                  <div className="grid gap-3 rounded-md border p-3">
                    <Label className="text-muted-foreground">Nachrichten – Agent</Label>
                    <ColorPickerField
                      label="Hintergrund"
                      value={agentMsgColor}
                      onChange={setAgentMsgColor}
                      defaultColor={WIDGET_DEFAULT_AGENT_MESSAGE_COLOR}
                    />
                    <ColorPickerField
                      label="Schrift"
                      value={agentMsgTextColor}
                      onChange={setAgentMsgTextColor}
                      defaultColor={contrastText(agentMsgColor ?? WIDGET_DEFAULT_AGENT_MESSAGE_COLOR)}
                      placeholder="Auto"
                    />
                  </div>
                  <div className="grid gap-3 rounded-md border p-3">
                    <Label className="text-muted-foreground">Nachrichten – Nutzer</Label>
                    <ColorPickerField
                      label="Hintergrund"
                      value={userMsgColor}
                      onChange={setUserMsgColor}
                      defaultColor={WIDGET_DEFAULT_USER_MESSAGE_COLOR}
                    />
                    <ColorPickerField
                      label="Schrift"
                      value={userMsgTextColor}
                      onChange={setUserMsgTextColor}
                      defaultColor={contrastText(userMsgColor ?? WIDGET_DEFAULT_USER_MESSAGE_COLOR)}
                      placeholder="Auto"
                    />
                  </div>

                  <div className="grid gap-2">
                    <Label>Position</Label>
                    <RadioGroup
                      value={position}
                      onValueChange={setPosition}
                      className="grid-cols-2"
                    >
                      {(['bottom-left', 'bottom-right'] as const).map((pos) => (
                        <Label
                          key={pos}
                          htmlFor={`pos-${pos}`}
                          className="flex cursor-pointer flex-col items-center gap-2 rounded-md border p-3 has-[:checked]:border-primary has-[:checked]:bg-muted/50"
                        >
                          <div className="relative h-14 w-full rounded bg-zinc-100">
                            <div
                              className={`absolute bottom-1.5 size-4 rounded-full bg-primary ${pos === 'bottom-left' ? 'left-1.5' : 'right-1.5'}`}
                            />
                          </div>
                          <div className="flex items-center gap-2">
                            <RadioGroupItem value={pos} id={`pos-${pos}`} />
                            <span className="text-sm font-normal">
                              {pos === 'bottom-left' ? 'Unten links' : 'Unten rechts'}
                            </span>
                          </div>
                        </Label>
                      ))}
                    </RadioGroup>
                  </div>

                  <div className="grid gap-2">
                    <Label>Logo</Label>
                    <LogoUpload
                      currentLogoUrl={selectedLogoUrl}
                      pendingFile={logoFile}
                      removeRequested={logoRemoveRequested}
                      onFileSelected={(file) => {
                        setLogoFile(file)
                        setLogoRemoveRequested(false)
                      }}
                      onRemove={() => {
                        setLogoFile(null)
                        setLogoRemoveRequested(true)
                      }}
                    />
                  </div>

                  <div>
                    <Button
                      type="button"
                      variant="link"
                      className="h-auto p-0 text-muted-foreground"
                      onClick={handleResetDesignToDefault}
                    >
                      Alles auf Standard zurücksetzen
                    </Button>
                  </div>
                </div>

                <div className="order-1 md:order-2">
                  <div className="md:sticky md:top-0">
                    <WidgetPreviewMock
                      bubbleColor={bubbleColor}
                      position={position}
                      agentMessageColor={agentMsgColor}
                      userMessageColor={userMsgColor}
                      agentMessageTextColor={agentMsgTextColor}
                      userMessageTextColor={userMsgTextColor}
                      backgroundColor={panelBgColor}
                      textColor={panelTextColor}
                      logoUrl={
                        logoPreviewUrl
                          ? logoPreviewUrl
                          : logoRemoveRequested
                            ? null
                            : selectedLogoUrl
                      }
                    />
                  </div>
                </div>
              </div>

              <DialogFooter className="mt-4">
                <Button
                  type="button"
                  variant="outline"
                  onClick={() => {
                    resetDesignState()
                    setDetailDialogOpen(false)
                  }}
                >
                  Abbrechen
                </Button>
                <Button type="button" onClick={handleDesignSave} disabled={!designDirty || isDesignSaving}>
                  {isDesignSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                  Speichern
                </Button>
              </DialogFooter>
            </TabsContent>
          </Tabs>
        </DialogContent>
      </Dialog>
    </PageContainer>
  )
}
